import { useMemo } from "react";
import { buildRemediationSummary } from "../../utils/remediationSummary.js";
import {
  distinctRemediationSteps,
  scrollToWorklistSection,
  worklistSectionId,
} from "../../utils/remediationDisplay.js";
import { NAVY } from "../report/reportNavyTokens.js";

const SEV_TAG = {
  CRITICAL: { label: "Critical", color: NAVY.crit },
  HIGH: { label: "High", color: NAVY.high },
  MEDIUM: { label: "Medium", color: NAVY.med },
  LOW: { label: "Low", color: NAVY.low },
};

const mono = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';
const display = '"Space Grotesk", "Inter", system-ui, sans-serif';
const body = '"Inter", system-ui, sans-serif';

export function FixBlock({ remediation }) {
  if (!remediation) return null;
  const summary = remediation.remediation_summary || "";
  const additiveSteps = distinctRemediationSteps(summary, remediation.remediation_steps);
  if (!summary && additiveSteps.length === 0) return null;

  return (
    <div
      style={{
        marginTop: 12,
        padding: "12px 14px",
        borderRadius: 8,
        border: `1px solid ${NAVY.line2}`,
        background: "rgba(91, 140, 255, 0.06)",
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: NAVY.elec, fontFamily: mono }}>
        FIX
      </div>
      {summary ? (
        <p style={{ margin: "6px 0 0", fontSize: 13, lineHeight: 1.55, color: NAVY.fg, fontFamily: body }}>
          {summary}
        </p>
      ) : null}
      {additiveSteps.length > 0 ? (
        <ol style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 12, lineHeight: 1.5, color: NAVY.mut, fontFamily: body }}>
          {additiveSteps.map((step, i) => (
            <li key={i} style={{ marginBottom: 4 }}>
              {step}
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

function WorklistCard({ finding, severity }) {
  const tag = SEV_TAG[severity] || SEV_TAG.MEDIUM;
  return (
    <article
      style={{
        border: `1px solid ${NAVY.line}`,
        borderRadius: 10,
        background: NAVY.panel2,
        overflow: "hidden",
        pageBreakInside: "avoid",
      }}
    >
      <div style={{ display: "flex", minHeight: "100%" }}>
        <div style={{ width: 4, flexShrink: 0, background: tag.color }} />
        <div style={{ flex: 1, padding: "14px 16px" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 6 }}>
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: tag.color,
                fontFamily: mono,
              }}
            >
              {tag.label}
            </span>
            {finding.rule_id ? (
              <span style={{ fontSize: 11, color: NAVY.faint, fontFamily: mono }}>{finding.rule_id}</span>
            ) : null}
          </div>
          <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: NAVY.fg, fontFamily: display, lineHeight: 1.35 }}>
            {finding.title || "Finding"}
          </h4>
          {finding.detail ? (
            <p style={{ margin: "8px 0 0", fontSize: 13, lineHeight: 1.55, color: NAVY.mut, fontFamily: body }}>
              {finding.detail}
            </p>
          ) : null}
          <FixBlock remediation={finding.remediation} />
          {finding.framework_line ? (
            <p style={{ margin: "10px 0 0", fontSize: 11, color: NAVY.faint, fontFamily: mono }}>
              Frameworks: {finding.framework_line}
            </p>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function CollapsedGroupRollup({ group }) {
  const oneLiners = (group.findings || [])
    .map((f) => f.remediation?.remediation_summary || f.title)
    .filter(Boolean)
    .slice(0, 6);
  return (
    <details
      style={{
        border: `1px solid ${NAVY.line}`,
        borderRadius: 10,
        background: NAVY.panel2,
        padding: "12px 14px",
      }}
    >
      <summary style={{ cursor: "pointer", listStyle: "none", fontFamily: body }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
          <span style={{ fontWeight: 700, color: NAVY.fg, fontSize: 14 }}>{group.display_label}</span>
          <span style={{ fontSize: 12, color: NAVY.mut }}>{group.count} item{group.count === 1 ? "" : "s"}</span>
        </div>
        <p style={{ margin: "6px 0 0", fontSize: 12, lineHeight: 1.45, color: NAVY.faint }}>
          {oneLiners.join(" · ")}
        </p>
      </summary>
      <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
        {(group.findings || []).map((f) => (
          <WorklistCard key={f.rule_id || f.title} finding={f} severity={group.severity} />
        ))}
      </div>
    </details>
  );
}

function HorizonAnchor({ sectionId, onNavigate, children, style }) {
  return (
    <button
      type="button"
      onClick={() => onNavigate(sectionId)}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        cursor: "pointer",
        textAlign: "inherit",
        font: "inherit",
        color: "inherit",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

/**
 * Exec brief + runway — used in sticky stack (in-app) and static export body.
 */
export function RemediationSummaryHero({ summary, compact = false, interactive = false }) {
  const brief = summary?.exec_brief;
  const caveat = summary?.caveat;
  if (!brief) return null;

  const onNavigate = (sectionId) => {
    if (interactive) scrollToWorklistSection(sectionId);
  };

  const pillGap = compact ? 12 : 16;
  const lanePad = compact ? "8px 8px" : "12px 10px";
  const laneCountSize = compact ? 18 : 22;

  return (
    <section
      className={compact ? "remediation-hero remediation-hero--compact" : "remediation-hero"}
      style={{
        padding: compact ? "14px 16px" : "18px 20px",
        borderRadius: 12,
        border: `1px solid ${NAVY.line}`,
        background: `linear-gradient(135deg, ${NAVY.panel} 0%, ${NAVY.panel2} 100%)`,
      }}
    >
      {caveat ? (
        <p style={{ margin: "0 0 12px", fontSize: 12, lineHeight: 1.5, color: NAVY.mut, fontStyle: "italic" }}>
          {caveat}
        </p>
      ) : null}
      <p
        className="remediation-hero-lead"
        style={{
          margin: 0,
          fontSize: compact ? 14 : 15,
          lineHeight: 1.55,
          color: NAVY.fg,
          fontFamily: display,
          fontWeight: 500,
        }}
      >
        {brief.lead_line}
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: pillGap, marginTop: compact ? 12 : 16 }}>
        <CountPill
          label="Fix now"
          count={brief.fix_now_count}
          color="#FF5964"
          sectionId={worklistSectionId("CRITICAL")}
          onNavigate={interactive ? onNavigate : null}
        />
        <CountPill
          label="This sprint"
          count={brief.this_sprint_count}
          color="#FF9F45"
          sectionId={worklistSectionId("HIGH")}
          onNavigate={interactive ? onNavigate : null}
        />
        <CountPill
          label="Planned"
          count={brief.plan_count}
          color="#F5C842"
          sectionId={worklistSectionId("MEDIUM")}
          onNavigate={interactive ? onNavigate : null}
        />
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: compact ? 8 : 10,
          marginTop: compact ? 12 : 18,
        }}
      >
        {(brief.runway || []).map((lane) => {
          const sectionId = worklistSectionId(lane.severity);
          const laneBody = (
            <>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: lane.color, fontFamily: mono }}>
                {lane.label.toUpperCase()}
              </div>
              <div style={{ marginTop: 6, fontSize: laneCountSize, fontWeight: 700, color: NAVY.fg, fontFamily: display }}>
                {lane.count}
              </div>
            </>
          );
          const laneStyle = {
            padding: lanePad,
            borderRadius: 8,
            border: `1px solid ${NAVY.line2}`,
            background: "rgba(0,0,0,0.2)",
            textAlign: "center",
            width: "100%",
          };
          return interactive ? (
            <HorizonAnchor key={lane.severity} sectionId={sectionId} onNavigate={onNavigate} style={laneStyle}>
              {laneBody}
            </HorizonAnchor>
          ) : (
            <div key={lane.severity} style={laneStyle}>
              {laneBody}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function RemediationSummaryWorklist({ summary }) {
  const groups = summary?.groups || [];
  return (
    <section>
      <h2
        style={{
          margin: "0 0 14px",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: NAVY.faint,
          fontFamily: mono,
        }}
      >
        Worklist
      </h2>
      <div style={{ display: "grid", gap: 20 }}>
        {groups.map((group) => {
          const sectionId = worklistSectionId(group.severity);
          return (
            <div key={group.urgency_key} className="remediation-worklist-section">
              <h3
                id={sectionId}
                className="remediation-worklist-anchor"
                style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 700, color: NAVY.fg, fontFamily: display }}
              >
                {group.display_label}
              </h3>
              {group.count === 0 ? (
                <p style={{ margin: 0, fontSize: 13, color: NAVY.ok, lineHeight: 1.5, fontStyle: "italic" }}>
                  {group.empty_message}
                </p>
              ) : group.collapsible ? (
                <CollapsedGroupRollup group={group} />
              ) : (
                <div style={{ display: "grid", gap: 10 }}>
                  {(group.findings || []).map((f) => (
                    <WorklistCard key={f.rule_id || f.title} finding={f} severity={group.severity} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Full static document (export/print layout).
 */
export default function RemediationSummaryDocument({ snapshot, providerId, project, tenant }) {
  const summary = useMemo(() => buildRemediationSummary(snapshot, providerId), [snapshot, providerId]);
  const providerName = project?.providerId ? String(project.providerId) : providerId || "—";
  const orgName = project?.org?.name || "Project";
  const env = project?.org?.environment || "";

  return (
    <div style={{ fontFamily: body, color: NAVY.fg }}>
      <header style={{ marginBottom: 24 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 28,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            fontFamily: display,
            color: NAVY.fg,
          }}
        >
          Remediation Summary
        </h1>
        <p style={{ margin: "8px 0 0", fontSize: 13, color: NAVY.mut }}>
          {orgName}
          {env ? ` — ${env}` : ""} · {providerName} · {formatSnapshotLabel(snapshot)}
        </p>
        {tenant ? (
          <p style={{ margin: "6px 0 0", fontSize: 12, color: NAVY.faint, fontFamily: mono }}>
            Tenant: {tenant}
          </p>
        ) : null}
      </header>

      <div style={{ marginBottom: 28 }}>
        <RemediationSummaryHero summary={summary} interactive={false} />
      </div>

      <RemediationSummaryWorklist summary={summary} />
    </div>
  );
}

function CountPill({ label, count, color, sectionId, onNavigate }) {
  const inner = (
    <>
      <span style={{ fontSize: 11, fontWeight: 700, color, fontFamily: mono, letterSpacing: "0.04em" }}>
        {label.toUpperCase()}
      </span>
      <span style={{ fontSize: 20, fontWeight: 700, color: NAVY.fg, fontFamily: display }}>{count}</span>
    </>
  );
  if (onNavigate && sectionId) {
    return (
      <HorizonAnchor
        sectionId={sectionId}
        onNavigate={onNavigate}
        style={{ display: "flex", alignItems: "baseline", gap: 8 }}
      >
        {inner}
      </HorizonAnchor>
    );
  }
  return <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>{inner}</div>;
}

function formatSnapshotLabel(snapshot) {
  const iso = snapshot?.takenAt || snapshot?.takenAtIso;
  if (!iso) return "Snapshot";
  try {
    return new Date(iso).toISOString().slice(0, 19).replace("T", " ") + " UTC";
  } catch {
    return "Snapshot";
  }
}
